"use client";

import React, { useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import Link from 'next/link';
import { cn } from '@/lib/utils';
import type { EstimatorSelections, PriceBreakdown } from './types';
import { buildSummary, FEATURE_PRICES } from './constants';

interface ResultsScreenProps {
  selections: EstimatorSelections;
  breakdown: PriceBreakdown;
  onStartOver: () => void;
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

const FEATURE_LABELS: Record<string, string> = {
  'event-calendar':   'Event calendar',
  'donation':         'Donation & payment',
  'member-portal':    'Member portal',
  'document-library': 'Document library',
  'multi-language':   'Multi-language',
  'blog':             'Blog / news',
  'contact-forms':    'Contact forms',
  'accessibility':    'WCAG/ADA accessibility',
  'seo':              'SEO setup',
  'cms-training':     'CMS training',
};

const PACKAGE_BADGES: Record<string, { label: string; icon: string; color: string }> = {
  Foundation: { label: 'Foundation Package', icon: 'Sprout',  color: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  Growth:     { label: 'Growth Package',     icon: 'Trees',   color: 'bg-blue-100 text-blue-800 border-blue-200'   },
  Signature:  { label: 'Signature Package',  icon: 'Flower2', color: 'bg-amber-100 text-amber-800 border-amber-200' },
};
import AnimatedIcon from '@/components/ui/AnimatedIcon';

const ResultsScreen: React.FC<ResultsScreenProps> = ({
  selections,
  breakdown,
  onStartOver,
}) => {
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    const t = setTimeout(() => headingRef.current?.focus(), 80);
    return () => clearTimeout(t);
  }, []);

  const summary = buildSummary(selections, breakdown.packageName);
  const pkg = PACKAGE_BADGES[breakdown.packageName] ?? PACKAGE_BADGES.Signature;

  const includedFeatures = selections.features.filter(
    (f) => FEATURE_PRICES[f] === 0,
  );
  const paidFeatures = selections.features.filter(
    (f) => FEATURE_PRICES[f] > 0,
  );

  return (
    <div className="w-full animate-fade-up">
      {/* ─── Package badge ─────────────────────────────────────── */}
      <div className="mb-6 flex items-center gap-3">
        <Badge
          className={cn(
            'font-sans font-semibold text-xs px-3 py-1.5 rounded-full border flex items-center gap-2',
            pkg.color,
          )}
        >
          <AnimatedIcon 
            name={pkg.icon} 
            size={16} 
            animation="pulse" 
            className="shrink-0" 
          />
          {pkg.label}
        </Badge>
      </div>

      {/* ─── Heading ───────────────────────────────────────────── */}
      <h2
        ref={headingRef}
        tabIndex={-1}
        className="font-serif text-3xl md:text-4xl font-bold text-[#1A412D] mb-4 leading-tight outline-none"
      >
        Here's your estimate.
      </h2>

      <p className="font-sans text-sm md:text-base text-[#7A7367] leading-relaxed mb-8 max-w-lg">
        {summary}
      </p>

      {/* ─── Price card ────────────────────────────────────────── */}
      <div className="bg-[#1A412D] rounded-3xl p-7 md:p-9 mb-6 shadow-xl shadow-[#1A412D]/15">
        <p className="font-sans text-xs font-semibold uppercase tracking-[0.15em] text-white/50 mb-2">
          Estimated Project Investment
        </p>
        <p className="font-serif text-4xl md:text-5xl font-bold text-white mb-1">
          {formatCurrency(breakdown.total)}
          <span className="text-2xl md:text-3xl font-semibold text-white/60">
            {' '}– {formatCurrency(breakdown.totalMax)}
          </span>
        </p>

        {breakdown.monthly !== null && breakdown.monthly > 0 && (
          <div className="mt-4 pt-4 border-t border-white/10">
            <p className="font-sans text-xs font-semibold uppercase tracking-[0.12em] text-white/50 mb-1">
              Monthly Care Plan
            </p>
            <p className="font-serif text-2xl font-bold text-[#B8883D]">
              {formatCurrency(breakdown.monthly)}
              {breakdown.monthlyMax !== null
                ? ` – ${formatCurrency(breakdown.monthlyMax)}`
                : ''}
              <span className="font-sans text-base font-normal text-white/60">/mo</span>
            </p>
          </div>
        )}

        {/* Breakdown line items */}
        <div className="mt-5 pt-5 border-t border-white/10 space-y-2">
          <div className="flex justify-between font-sans text-xs text-white/50">
            <span>Base ({selections.pageCount} pages)</span>
            <span>{formatCurrency(breakdown.base)}</span>
          </div>
          {breakdown.addons > 0 && (
            <div className="flex justify-between font-sans text-xs text-white/50">
              <span>Feature add-ons</span>
              <span>+{formatCurrency(breakdown.addons)}</span>
            </div>
          )}
          {breakdown.content > 0 && (
            <div className="flex justify-between font-sans text-xs text-white/50">
              <span>Content creation</span>
              <span>+{formatCurrency(breakdown.content)}</span>
            </div>
          )}
        </div>
      </div>

      {/* ─── Feature summary ───────────────────────────────────── */}
      {selections.features.length > 0 && (
        <div className="bg-[#F8F5F1] rounded-2xl p-5 mb-6 space-y-4">
          {paidFeatures.length > 0 && (
            <div>
              <p className="font-sans text-xs font-semibold uppercase tracking-[0.1em] text-[#7A7367] mb-2">
                Add-ons included
              </p>
              <div className="flex flex-wrap gap-2">
                {paidFeatures.map((f) => (
                  <span
                    key={f}
                    className="inline-flex items-center gap-1 font-sans text-xs font-medium bg-white border border-[#E8E2D9] text-[#1A412D] px-3 py-1 rounded-full"
                  >
                    {FEATURE_LABELS[f]} · +{formatCurrency(FEATURE_PRICES[f])}
                  </span>
                ))}
              </div>
            </div>
          )}
          {includedFeatures.length > 0 && (
            <div>
              <p className="font-sans text-xs font-semibold uppercase tracking-[0.1em] text-[#7A7367] mb-2">
                Always included at no extra cost
              </p>
              <div className="flex flex-wrap gap-2">
                {includedFeatures.map((f) => (
                  <span
                    key={f}
                    className="inline-flex items-center gap-1.5 font-sans text-xs font-medium bg-emerald-50 border border-emerald-100 text-emerald-700 px-3 py-1 rounded-full"
                  >
                    <svg viewBox="0 0 12 12" fill="none" className="w-3 h-3" aria-hidden="true">
                      <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    {FEATURE_LABELS[f]}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ─── CTAs ──────────────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row items-center gap-3 mb-6">
        <Button
          asChild
          className={cn(
            'w-full sm:w-auto flex-1 font-sans font-semibold px-7 py-3 rounded-xl text-sm',
            'bg-[#1A412D] text-white hover:bg-[#142e1f] shadow-md',
            'focus-visible:ring-2 focus-visible:ring-[#1A412D] focus-visible:ring-offset-2',
          )}
        >
          <Link href="/book">
            Let's Talk About Your Project
            <svg viewBox="0 0 16 16" fill="none" className="w-4 h-4 ml-2 inline-block" aria-hidden="true">
              <path d="M6 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </Link>
        </Button>
        <Button
          asChild
          variant="outline"
          className={cn(
            'w-full sm:w-auto font-sans font-medium px-7 py-3 rounded-xl text-sm',
            'border-[#E8E2D9] text-[#1A412D] hover:bg-[#F8F5F1] hover:border-[#1A412D]/30',
            'focus-visible:ring-2 focus-visible:ring-[#1A412D] focus-visible:ring-offset-2',
          )}
        >
          <a href="mailto:hello@laurelweb.co">Email Us Instead</a>
        </Button>
      </div>

      {/* ─── Disclaimer ────────────────────────────────────────── */}
      <p className="font-sans text-xs text-[#7A7367] italic text-center mb-6 leading-relaxed max-w-md mx-auto">
        This is a ballpark estimate. Every project is unique. Let's chat to nail down exact scope and pricing.
      </p>

      {/* ─── Start over ────────────────────────────────────────── */}
      <div className="text-center">
        <button
          onClick={onStartOver}
          className="font-sans text-xs text-[#7A7367] hover:text-[#1A412D] underline underline-offset-4 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1A412D] focus-visible:ring-offset-2 rounded"
          aria-label="Start the estimator over from the beginning"
        >
          Start over
        </button>
      </div>
    </div>
  );
};

export default ResultsScreen;
